import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

// Animations
export const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

export const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// Base Chart Container
export const StyledChartContainer = styled(motion.div)`
  position: relative;
  background: ${({ theme }) => theme.colors.cardBackground || '#ffffff'};
  border-radius: 20px;
  padding: 24px;
  margin: 16px 0;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.primary || '#6366f1'},
      transparent
    );
    animation: ${shimmer} 3s infinite;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 16px;
    margin: 12px 0;
  }
`;

export const StyledChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 16px;
  }
`;

export const StyledChartTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary || '#1f2937'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const StyledChartInfo = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 12px;
    flex-wrap: wrap;
  }
`;

export const StyledMetric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 12px;
  min-width: 80px;

  @media (max-width: 768px) {
    min-width: 70px;
    padding: 6px 10px;
  }
`;

export const StyledMetricLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary || '#6b7280'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const StyledMetricValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary || '#1f2937'};
  margin-top: 2px;
`;

// Custom Tooltip
export const StyledTooltip = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground || '#ffffff'};
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(20px);
  min-width: 160px;

  .tooltip-label {
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textSecondary || '#6b7280'};
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .tooltip-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  .tooltip-color {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .tooltip-value {
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textPrimary || '#1f2937'};
  }

  .tooltip-name {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSecondary || '#6b7280'};
    margin-left: auto;
  }
`;
